import React, { useState, useEffect } from "react";
import { realTimeDb } from "../firebase";
import { ref, get, update } from "firebase/database";
import { useNavigate } from "react-router-dom";
import "./AdminDashboard.css";
import {
  PLAN_CONFIG,
  PLAN_ORDER,
  DEFAULT_PLAN,
  getPlanLimit,
  isUnlimitedLimit,
  formatLimitLabel
} from "../config/planConfig";

const AdminDashboard = () => {
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [linkLoading, setLinkLoading] = useState({});
  const [usageResetting, setUsageResetting] = useState({});
  const [paymentConfig, setPaymentConfig] = useState({
    merchantId: '',
    merchantKey: '',
    passphrase: '',
    notifyUrl: '',
    mode: 'sandbox'
  });
  const [paymentLoading, setPaymentLoading] = useState(true);
  const [paymentSaving, setPaymentSaving] = useState(false);
  const [passwordResetting, setPasswordResetting] = useState({});
  const navigate = useNavigate();
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || "https://api-ebz57wajla-uc.a.run.app";
  const adminApiToken = process.env.REACT_APP_ADMIN_API_TOKEN || '';

  useEffect(() => {
    const isAdmin = localStorage.getItem("isAdmin") === "true";

    if (!isAdmin) {
      alert("You must be logged in to access this page.");
      navigate("/adminlogin");
      return;
    }

    const fetchUsers = async () => {
      console.log("Fetching users...");

      try {
        const usersRef = ref(realTimeDb, "users");
        const snapshot = await get(usersRef);

        if (snapshot.exists()) {
          const usersList = await Promise.all(
            Object.entries(snapshot.val()).map(async ([id, data]) => {
              const submissionsRef = ref(realTimeDb, `users/${id}/submissions`);
              const submissionsSnap = await get(submissionsRef);

              const submissionCount = submissionsSnap.exists()
                ? Object.keys(submissionsSnap.val()).length
                : 0;

              const planKey = PLAN_CONFIG[data.plan] ? data.plan : DEFAULT_PLAN;
              const monthlyLimit = data.monthlyLimit !== undefined ? data.monthlyLimit : getPlanLimit(planKey);
              const usage = data.usage || {};
              const usageMonth = usage.month || new Date().toISOString().slice(0, 7);
              const usageCount = usage.count || 0;

              return {
                id,
                email: data.email || "No Email",
                name: data.name || "No Name",
                isSuspended: data.isSuspended || false,
                plan: planKey,
                monthlyLimit,
                billingStatus: data.billingStatus || "active",
                usage: {
                  month: usageMonth,
                  count: usageCount
                },
                submissionCount,
                adminTempPassword: data.adminTempPassword || '',
                subscriptionId: data.billing?.payfast?.subscriptionId || data.subscriptionId || null,
                lastPaymentAt: data.billing?.payfast?.lastPaymentAt || null,
                paymentStatus: data.billing?.payfast?.paymentStatus || null
              };
            })
          );

          setUsers(usersList);
        } else {
          console.log("No users found in Realtime Database.");
          setUsers([]);
        }
      } catch (error) {
        console.error("Error fetching users:", error);
        alert("Failed to load users. Check Firebase rules or database.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchUsers();
    fetchPaymentSettings();
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem("isAdmin");
    navigate("/adminlogin");
  };

  const toggleAccess = async (userId, isSuspended) => {
    try {
      const userRef = ref(realTimeDb, `users/${userId}`);
      await update(userRef, { isSuspended: !isSuspended });

      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          user.id === userId ? { ...user, isSuspended: !isSuspended } : user
        )
      );
      alert(`User ${isSuspended ? "granted" : "denied"} access successfully.`);
    } catch (error) {
      console.error("Error updating user access:", error);
      alert("Failed to update user access. Please try again.");
    }
  };

  const resetUserPassword = async (user) => {
    if (!adminApiToken) {
      alert('Admin API token is not configured.');
      return;
    }

    const confirmReset = window.confirm(`Generate a new password for ${user.email}?`);
    if (!confirmReset) {
      return;
    }

    setPasswordResetting((prev) => ({ ...prev, [user.id]: true }));

    try {
      const response = await fetch(`${apiBaseUrl}/admin/users/${user.id}/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-token': adminApiToken
        }
      });

      if (!response.ok) {
        const message = await response.text();
        throw new Error(message || 'Failed to reset password');
      }

      const payload = await response.json();
      const newPassword = payload?.password || '';

      setUsers((prev) =>
        prev.map((item) =>
          item.id === user.id
            ? { ...item, adminTempPassword: newPassword }
            : item
        )
      );

      if (newPassword) {
        alert(`New password for ${user.email}: ${newPassword}`);
      } else {
        alert('Password reset but no password returned.');
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      alert('Failed to reset password. Please try again.');
    } finally {
      setPasswordResetting((prev) => ({ ...prev, [user.id]: false }));
    }
  };

  const fetchPaymentSettings = async () => {
    try {
      const settingsRef = ref(realTimeDb, 'settings/payfast');
      const snapshot = await get(settingsRef);
      if (snapshot.exists()) {
        const data = snapshot.val();
        setPaymentConfig((prev) => ({
          ...prev,
          ...data
        }));
      }
    } catch (error) {
      console.error('Error fetching payment settings:', error);
    } finally {
      setPaymentLoading(false);
    }
  };

  const handlePaymentConfigChange = (field, value) => {
    setPaymentConfig((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const savePaymentSettings = async (event) => {
    event.preventDefault();

    if (!paymentConfig.merchantId || !paymentConfig.merchantKey) {
      alert('Merchant ID and Merchant Key are required.');
      return;
    }

    setPaymentSaving(true);
    try {
      const settingsRef = ref(realTimeDb, 'settings/payfast');
      await update(settingsRef, {
        merchantId: paymentConfig.merchantId,
        merchantKey: paymentConfig.merchantKey,
        passphrase: paymentConfig.passphrase || '',
        notifyUrl: paymentConfig.notifyUrl || '',
        mode: paymentConfig.mode || 'sandbox'
      });
      alert('Payment settings saved successfully.');
    } catch (error) {
      console.error('Error saving payment settings:', error);
      alert('Failed to save payment settings.');
    } finally {
      setPaymentSaving(false);
    }
  };

  const updateUserPlan = async (userId, nextPlan) => {
    if (!PLAN_CONFIG[nextPlan]) {
      alert("Invalid plan selection.");
      return;
    }

    try {
      const planMeta = PLAN_CONFIG[nextPlan];
      
      // Use the backend API endpoint for better validation and consistency
      const response = await fetch(`${apiBaseUrl}/admin/update-user-plan`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Token': adminApiToken
        },
        body: JSON.stringify({
          userId: userId,
          planId: nextPlan,
          subscriptionId: `admin_manual_${Date.now()}`
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update plan');
      }

      const result = await response.json();
      
      // Update local state to reflect the changes
      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          user.id === userId
            ? {
                ...user,
                plan: nextPlan,
                monthlyLimit: planMeta.limit,
                billingStatus: 'active',
                subscriptionId: `admin_manual_${Date.now()}`,
                paymentStatus: 'COMPLETE',
                lastPaymentAt: new Date().toISOString()
              }
            : user
        )
      );
      
      alert(`Plan successfully updated to ${planMeta.name}!`);
    } catch (error) {
      console.error("Error updating user plan:", error);
      alert(`Failed to update plan: ${error.message}`);
    }
  };

  const resetUserUsage = async (userId) => {
    setUsageResetting((prev) => ({ ...prev, [userId]: true }));
    const currentMonth = new Date().toISOString().slice(0, 7);
    try {
      const usageRef = ref(realTimeDb, `users/${userId}/usage`);
      await update(usageRef, { month: currentMonth, count: 0 });

      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          user.id === userId
            ? {
                ...user,
                usage: {
                  month: currentMonth,
                  count: 0
                }
              }
            : user
        )
      );
      alert("Usage counter reset for this month.");
    } catch (error) {
      console.error("Error resetting usage:", error);
      alert("Failed to reset usage. Please try again.");
    } finally {
      setUsageResetting((prev) => ({ ...prev, [userId]: false }));
    }
  };

  const generatePayfastLink = async (userRecord, planId) => {
    const targetPlan = PLAN_CONFIG[planId] ? planId : userRecord.plan;
    const planMeta = PLAN_CONFIG[targetPlan];

    if (!planMeta || planMeta.priceMonthly === 0) {
      alert("This plan does not require a payment link.");
      return;
    }

    if (!paymentConfig.merchantId || !paymentConfig.merchantKey) {
      alert('Please configure PayFast credentials before generating a link.');
      return;
    }

    setLinkLoading((prev) => ({ ...prev, [userRecord.id]: true }));

    try {
      const response = await fetch(`${apiBaseUrl}/payfast/initiate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          plan: targetPlan,
          userId: userRecord.id,
          email: userRecord.email,
          returnUrl: `${window.location.origin}/dashboard`,
          cancelUrl: `${window.location.origin}/dashboard`,
          ...paymentConfig
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      // Check if response is HTML (PayFast form) or JSON (error/free plan)
      const contentType = response.headers.get('content-type');
      
      if (contentType && contentType.includes('text/html')) {
        // Create a new window/tab to display the PayFast form
        const htmlContent = await response.text();
        const newWindow = window.open('', '_blank', 'width=800,height=600');
        if (newWindow) {
          newWindow.document.write(htmlContent);
          newWindow.document.close();
        } else {
          alert('Please allow popups to open the PayFast payment window.');
        }
      } else {
        // Handle JSON response
        const data = await response.json();
        if (data.message) {
          alert(data.message);
        } else if (data.redirectUrl) {
          window.open(data.redirectUrl, "_blank", "noopener,noreferrer");
        } else {
          alert("No redirect URL returned by the payment service.");
        }
      }
    } catch (error) {
      console.error("Error generating PayFast link:", error);
      alert("Failed to generate PayFast link. Please try again.");
    } finally {
      setLinkLoading((prev) => ({ ...prev, [userRecord.id]: false }));
    }
  };

  return (
    <div className="admin-dashboard-page">
      <div className="admin-sidebar">
        <h2>Admin Dashboard</h2>
        <ul>
          <li>Manage Users</li>
          <li>Manage Permissions</li>
          <li>System Settings</li>
        </ul>
        <button onClick={handleLogout} className="admin-logout-btn">
          Logout
        </button>
      </div>

      <div className="admin-main-content">
        <h1>Admin Control Panel</h1>

        <section className="admin-section">
          <h2>PayFast Configuration</h2>
          {paymentLoading ? (
            <p>Loading payment settings...</p>
          ) : (
            <form className="payfast-config-form" onSubmit={savePaymentSettings} autoComplete="off">
              <div className="form-row">
                <label htmlFor="merchantId">Merchant ID</label>
                <input
                  id="merchantId"
                  type="text"
                  value={paymentConfig.merchantId}
                  onChange={(event) => handlePaymentConfigChange('merchantId', event.target.value)}
                  required
                  disabled={paymentSaving}
                />
              </div>

              <div className="form-row">
                <label htmlFor="merchantKey">Merchant Key</label>
                <input
                  id="merchantKey"
                  type="text"
                  value={paymentConfig.merchantKey}
                  onChange={(event) => handlePaymentConfigChange('merchantKey', event.target.value)}
                  required
                  disabled={paymentSaving}
                />
              </div>

              <div className="form-row">
                <label htmlFor="passphrase">Passphrase (optional)</label>
                <input
                  id="passphrase"
                  type="password"
                  value={paymentConfig.passphrase || ''}
                  onChange={(event) => handlePaymentConfigChange('passphrase', event.target.value)}
                  disabled={paymentSaving}
                />
              </div>

              <div className="form-row">
                <label htmlFor="notifyUrl">Notify URL</label>
                <input
                  id="notifyUrl"
                  type="url"
                  placeholder="https://example.com/payfast/ipn"
                  value={paymentConfig.notifyUrl || ''}
                  onChange={(event) => handlePaymentConfigChange('notifyUrl', event.target.value)}
                  disabled={paymentSaving}
                />
              </div>

              <div className="form-row">
                <label htmlFor="mode">Mode</label>
                <select
                  id="mode"
                  value={paymentConfig.mode || 'sandbox'}
                  onChange={(event) => handlePaymentConfigChange('mode', event.target.value)}
                  disabled={paymentSaving}
                >
                  <option value="sandbox">Sandbox</option>
                  <option value="production">Production</option>
                </select>
              </div>

              <div className="form-actions">
                <button type="submit" disabled={paymentSaving}>
                  {paymentSaving ? 'Saving...' : 'Save Settings'}
                </button>
              </div>
            </form>
          )}
        </section>

        <h2>Registered Users</h2>
        {isLoading ? (
          <p>Loading users...</p>
        ) : users.length > 0 ? (
          <table className="admin-goals-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Name</th>
                <th>Status</th>
                <th>Plan Management</th>
                <th>Usage</th>
                <th>Billing & Subscription</th>
                <th>Submissions</th>
                <th>Password</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td>{user.email}</td>
                  <td>{user.name}</td>
                  <td>{user.isSuspended ? "Denied" : "Granted"}</td>
                  <td>
                    <div style={{ marginBottom: "8px" }}>
                      <select
                        value={user.plan}
                        onChange={(e) => updateUserPlan(user.id, e.target.value)}
                        style={{ padding: "6px 8px", borderRadius: "6px", border: "1px solid #cbd5e1", width: "100%" }}
                      >
                        {PLAN_ORDER.map((planId) => (
                          <option key={planId} value={planId}>
                            {PLAN_CONFIG[planId].name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div style={{ fontSize: "12px", color: "#64748b" }}>
                      Current: <strong>{PLAN_CONFIG[user.plan].name}</strong>
                    </div>
                    <div style={{ fontSize: "12px", color: "#64748b" }}>
                      Limit: {isUnlimitedLimit(user.monthlyLimit) ? "Unlimited" : `${formatLimitLabel(user.monthlyLimit)} / month`}
                    </div>
                  </td>
                  <td>
                    <div>
                      {user.usage.count} used
                      {!isUnlimitedLimit(user.monthlyLimit) && (
                        <> / {formatLimitLabel(user.monthlyLimit)} submissions</>
                      )}
                    </div>
                    <div style={{ fontSize: "12px", color: "#64748b" }}>Month: {user.usage.month}</div>
                  </td>
                  <td>
                    <div style={{ marginBottom: "4px" }}>
                      <strong>Status:</strong> <span style={{ 
                        color: user.billingStatus === 'active' ? '#16a34a' : 
                              user.billingStatus === 'cancelled' ? '#dc2626' : '#ca8a04' 
                      }}>
                        {user.billingStatus}
                      </span>
                    </div>
                    {user.subscriptionId && (
                      <div style={{ fontSize: "12px", color: "#64748b", marginBottom: "4px" }}>
                        <strong>Sub ID:</strong> {user.subscriptionId}
                      </div>
                    )}
                    {user.paymentStatus && (
                      <div style={{ fontSize: "12px", color: "#64748b", marginBottom: "4px" }}>
                        <strong>Payment:</strong> {user.paymentStatus}
                      </div>
                    )}
                    {user.lastPaymentAt && (
                      <div style={{ fontSize: "12px", color: "#64748b" }}>
                        <strong>Last Payment:</strong> {new Date(user.lastPaymentAt).toLocaleDateString()}
                      </div>
                    )}
                  </td>
                  <td>
                    {user.submissionCount > 0 ? `${user.submissionCount} total` : <em>No submissions</em>}
                  </td>
                  <td>
                    {user.adminTempPassword ? (
                      <code style={{ backgroundColor: '#f1f5f9', padding: '4px 6px', borderRadius: '4px' }}>
                        {user.adminTempPassword}
                      </code>
                    ) : (
                      <em>Not set</em>
                    )}
                  </td>
                  <td>
                    <button
                      onClick={() => toggleAccess(user.id, user.isSuspended)}
                      className={user.isSuspended ? "grant-btn" : "deny-btn"}
                    >
                      {user.isSuspended ? "Grant Access" : "Deny Access"}
                    </button>
                    <button
                      onClick={() => resetUserUsage(user.id)}
                      className="grant-btn"
                      disabled={usageResetting[user.id]}
                      style={{ marginTop: "8px" }}
                    >
                      {usageResetting[user.id] ? "Resetting..." : "Reset Usage"}
                    </button>
                    <button
                      onClick={() => resetUserPassword(user)}
                      className="grant-btn"
                      disabled={passwordResetting[user.id]}
                      style={{ marginTop: "8px" }}
                    >
                      {passwordResetting[user.id] ? "Updating..." : "Reset Password"}
                    </button>
                    <button
                      onClick={() => generatePayfastLink(user, user.plan)}
                      className="deny-btn"
                      disabled={linkLoading[user.id]}
                      style={{ marginTop: "8px" }}
                    >
                      {linkLoading[user.id] ? "Generating..." : "PayFast Link"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p>No users found.</p>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
